import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Button from '@/components/CustomButtons/Button';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';
import { API_BASE_URL } from '@/config';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import UploadIcon from '@mui/icons-material/Upload';
import moment from 'moment';
import { toast } from 'react-toastify';

const PositivePayFiles = ({ smartPayablesAccountId }) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getFiles = async () => {
    if (!smartPayablesAccountId) return;

    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/smartpayables/${smartPayablesAccountId}/positive-pay-files?page=${
          page + 1
        }&limit=${rowsPerPage}&sort=created_at&order=DESC`,
      );

      if (response.data.success) {
        setFiles(response.data.data.positive_pay_files);
        setTotalCount(response.data.data.pagination.total_count);
      }
    } catch (error) {
      console.error('Error fetching positive pay files:', error);
      setFiles([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAndUpload = async () => {
    if (!smartPayablesAccountId) return;

    try {
      setGenerating(true);
      const response = await axios.post(
        `${API_BASE_URL}/smartpayables/${smartPayablesAccountId}/generate-and-upload`,
        null,
        { skipSuccessToast: true },
      );
      if (response.data?.message) {
        toast.success(
          <span data-testid='positive-pay-started-toast'>
            {response.data.message}
          </span>,
          { position: 'bottom-right', autoClose: 5000 },
        );
      }
      await getFiles();
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (smartPayablesAccountId) {
      getFiles();
    }
  }, [smartPayablesAccountId, page, rowsPerPage]);

  if (!smartPayablesAccountId) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
        <h3>{t('positivePayFiles.noAccountSelected')}</h3>
      </div>
    );
  }

  return (
    <div style={{ marginTop: '20px' }}>
      <div
        style={{
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'flex-end',
        }}>
        <Button
          variant='contained'
          color='primary'
          startIcon={
            generating ? (
              <CircularProgress size={20} style={{ color: 'white' }} />
            ) : (
              <UploadIcon style={{ color: 'white' }} />
            )
          }
          onClick={handleGenerateAndUpload}
          disabled={generating || !smartPayablesAccountId}
          style={{
            textTransform: 'none',
            color: 'white',
          }}>
          {generating
            ? t('positivePayFiles.generating')
            : t('positivePayFiles.generateButton')}
        </Button>
      </div>
      <div style={{ overflow: 'auto' }}>
        <Table width='100%'>
          <TableHead>
            <TableRow style={{ backgroundColor: '#f5f5f5' }}>
              <TableCell
                style={{
                  padding: '12px',
                  fontWeight: 'bold',
                  borderBottom: '1px solid #ddd',
                }}>
                {t('positivePayFiles.table.createdAt')}
              </TableCell>
              <TableCell
                style={{
                  padding: '12px',
                  fontWeight: 'bold',
                  borderBottom: '1px solid #ddd',
                }}>
                {t('positivePayFiles.table.generatedBy')}
              </TableCell>
              <TableCell
                style={{
                  padding: '12px',
                  fontWeight: 'bold',
                  borderBottom: '1px solid #ddd',
                }}>
                {t('positivePayFiles.table.noRefunds')}
              </TableCell>
              <TableCell
                style={{
                  padding: '12px',
                  fontWeight: 'bold',
                  borderBottom: '1px solid #ddd',
                }}>
                {t('positivePayFiles.table.reference')}
              </TableCell>
              <TableCell
                style={{
                  padding: '12px',
                  fontWeight: 'bold',
                  borderBottom: '1px solid #ddd',
                }}>
                {t('positivePayFiles.table.totalAmount')}
              </TableCell>
              <TableCell
                style={{
                  padding: '12px',
                  fontWeight: 'bold',
                  borderBottom: '1px solid #ddd',
                }}>
                {t('positivePayFiles.table.fileName')}
              </TableCell>
              <TableCell
                style={{
                  padding: '12px',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  borderBottom: '1px solid #ddd',
                }}>
                {t('positivePayFiles.table.download')}
              </TableCell>
            </TableRow>
          </TableHead>
          {files && files.length > 0 ? (
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    style={{
                      padding: '20px',
                      textAlign: 'center',
                      color: '#666',
                    }}>
                    {t('positivePayFiles.table.loading')}
                  </TableCell>
                </TableRow>
              ) : (
                files.map((obj, index) => (
                  <TableRow
                    key={index}
                    style={{ borderBottom: '1px solid #eee' }}>
                    <TableCell style={{ padding: '12px' }}>
                      {obj.created_at
                        ? moment(obj.created_at).format('MM/DD/YYYY hh:mm A')
                        : '-'}
                    </TableCell>
                    <TableCell style={{ padding: '12px' }}>
                      {obj && obj.created_by}
                    </TableCell>
                    <TableCell style={{ padding: '12px' }}>
                      {obj.no_of_refunds}
                    </TableCell>
                    <TableCell style={{ padding: '12px' }}>
                      {obj.reference_code ?? ''}
                    </TableCell>
                    <TableCell style={{ padding: '12px' }}>
                      ${parseFloat(obj.total_amount ?? 0).toFixed(2)}
                    </TableCell>
                    <TableCell style={{ padding: '12px' }}>
                      {obj.file_name}
                    </TableCell>
                    <TableCell style={{ padding: '12px', textAlign: 'center' }}>
                      <a
                        style={{
                          cursor: 'pointer',
                          color: '#1976d2',
                        }}
                        href={
                          API_BASE_URL + '/getFile?filePath=' + obj.file_url
                        }>
                        <CloudDownloadIcon color='primary' />
                      </a>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          ) : (
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={7}
                  style={{
                    padding: '20px',
                    textAlign: 'center',
                    color: '#666',
                  }}>
                  {loading
                    ? t('positivePayFiles.table.loading')
                    : t('positivePayFiles.table.noFiles')}
                </TableCell>
              </TableRow>
            </TableBody>
          )}
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                colSpan={7}
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                SelectProps={{
                  inputProps: { 'aria-label': 'rows per page' },
                  native: true,
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </div>
  );
};

export default PositivePayFiles;
